import React, { useState } from 'react';
import { Camera, Upload, Mic, Square, Check, X, Sparkles, Volume2 } from 'lucide-react';

export interface CustomAACTile {
  id: string;
  label: string;
  category: 'pronoun' | 'verb' | 'noun' | 'adjective' | 'social';
  imageUri?: string;
  audioBlobUrl?: string;
  icon?: string;
}

interface CustomTileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (tile: CustomAACTile) => void;
}

export const CustomTileModal: React.FC<CustomTileModalProps> = ({ isOpen, onClose, onSave }) => {
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState<'pronoun' | 'verb' | 'noun' | 'adjective' | 'social'>('noun');
  const [imageUri, setImageUri] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  if (!isOpen) return null;

  // Handle local file / photo picker
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageUri(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle Audio Recording via browser MediaRecorder
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (err) {
      console.warn('Microphone access unavailable, using speech synthesis fallback', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const handleSave = () => {
    if (!label.trim()) return;

    const newTile: CustomAACTile = {
      id: `custom_${Date.now()}`,
      label: label.trim().toUpperCase(),
      category,
      imageUri: imageUri || undefined,
      audioBlobUrl: audioUrl || undefined,
      icon: '⭐'
    };

    onSave(newTile);
    onClose();
  };

  const playRecordedAudio = () => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play();
    } else if ('speechSynthesis' in window) {
      const u = new SpeechSynthesisUtterance(label);
      window.speechSynthesis.speak(u);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
      <div className="bg-[#1c1b1f] text-white w-full max-w-md rounded-[32px] border border-white/15 p-6 shadow-2xl space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-xl bg-[#ffd64e] text-black font-black text-xs">
              <Camera className="w-4 h-4" />
            </span>
            <div>
              <h3 className="text-base font-black text-white">1-Tap Custom AAC Tile</h3>
              <p className="text-[11px] text-white/60">Snap a real photo & record personalized speech</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-1.5 rounded-full text-white/40 hover:text-white hover:bg-white/10 transition cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Fields */}
        <div className="space-y-4">
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-white/60 block mb-1">
              Word or Phrase Label
            </label>
            <input 
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. JUICE, DAD, FAVORITE BLANKET"
              className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/15 text-white font-bold text-sm focus:outline-none focus:border-[#ffd64e]"
            />
          </div>

          {/* Fitzgerald Category Selector */}
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-white/60 block mb-1">
              Clinical Fitzgerald Key Category
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'pronoun', label: 'Pronoun (Yellow)', color: 'bg-amber-400 text-black' },
                { id: 'verb', label: 'Action Verb (Green)', color: 'bg-emerald-500 text-white' },
                { id: 'noun', label: 'Noun / Object (Orange)', color: 'bg-orange-500 text-white' },
                { id: 'adjective', label: 'Descriptor (Blue)', color: 'bg-sky-500 text-white' },
                { id: 'social', label: 'Social Phrase (Pink)', color: 'bg-pink-500 text-white' }
              ].map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategory(c.id as any)}
                  className={`py-2 px-2.5 rounded-xl text-[10px] font-black transition-all border cursor-pointer ${
                    category === c.id 
                      ? `${c.color} ring-2 ring-white border-transparent scale-102` 
                      : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Photo / Camera Capture */}
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-white/60 block mb-1">
              Tile Photo / Symbol
            </label>
            <div className="flex items-center gap-3">
              <label className="flex-1 py-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-dashed border-white/20 text-white text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition">
                <Upload className="w-4 h-4 text-[#ffd64e]" />
                <span>Upload / Take Photo</span>
                <input type="file" accept="image/*" capture="environment" onChange={handleFileChange} className="hidden" />
              </label>

              {imageUri && (
                <div className="w-12 h-12 rounded-2xl overflow-hidden border border-[#ffd64e] shrink-0">
                  <img src={imageUri} alt="Preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          </div>

          {/* Voice Audio Recording */}
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-white/60 block mb-1">
              Personalized Voice Audio
            </label>
            <div className="flex items-center gap-2">
              {!isRecording ? (
                <button
                  type="button"
                  onClick={startRecording}
                  className="flex-1 py-2.5 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/15 text-white text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition"
                >
                  <Mic className="w-4 h-4 text-emerald-400" />
                  <span>{audioUrl ? 'Re-record Custom Voice' : 'Record Real Voice'}</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={stopRecording}
                  className="flex-1 py-2.5 rounded-2xl bg-rose-600 text-white text-xs font-bold flex items-center justify-center gap-2 cursor-pointer animate-pulse"
                >
                  <Square className="w-4 h-4 fill-white" />
                  <span>Stop Recording Audio</span>
                </button>
              )}

              {audioUrl && (
                <button
                  type="button"
                  onClick={playRecordedAudio}
                  className="p-2.5 rounded-2xl bg-[#ffd64e] text-black font-black hover:bg-amber-400 transition cursor-pointer"
                  title="Test Recorded Audio"
                >
                  <Volume2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-full text-xs font-bold text-white/70 hover:text-white hover:bg-white/10 transition cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!label.trim()}
            className="px-6 py-2.5 rounded-full bg-[#ffd64e] hover:bg-amber-400 disabled:opacity-40 text-black font-black text-xs shadow-md transition cursor-pointer flex items-center gap-1.5"
          >
            <Check className="w-4 h-4" />
            <span>Add to Active AAC Grid</span>
          </button>
        </div>

      </div>
    </div>
  );
};
